/**
 * Tour step definitions.
 *
 * Each tour is a named array of steps. Side effects (navigation, editor reset)
 * are defined as onNext/beforeShow hooks on each step, keeping the engine generic.
 */
import { TourStep, TourStepTypeE } from "@/composables/useTour";
import router from "@/router";
import { useEventBus } from "@vueuse/core";
import { nextTick } from "vue";
import { sleep } from "@/js/utilities";
import { tourEventKey } from "@/eventBus/tourEventBus";

const tourBus = useEventBus(tourEventKey);

async function navigateTo(path: string) {
	if (router.currentRoute.value.path !== path) {
		await router.push(path);
		await nextTick();
	}
}

async function resetEditorCode() {
	tourBus.emit({ type: "reset-editor" });
	await nextTick();
}

function waitForElement(selector: string, scrollIntoView = false, timeoutMs = 10000): Promise<Element | null> {
	return new Promise((resolve) => {
		const existing = document.querySelector(selector);
		if (existing) {
			if (scrollIntoView) existing.scrollIntoView({ behavior: "smooth", block: "center" });
			resolve(existing);
			return;
		}
		const observer = new MutationObserver(() => {
			const el = document.querySelector(selector);
			if (el) {
				observer.disconnect();
				clearTimeout(timer);
				if (scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
				resolve(el);
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
		const timer = setTimeout(() => {
			observer.disconnect();
			resolve(null);
		}, timeoutMs);
	});
}

/*
 *	Reveals a line in Monaco and finds the rendered .view-line DOM element by text content,
 *	because Monaco's decoration API places overlays in a separate container with unreliable dimensions.
 */
const TOUR_LINE_CLASS = "tour-target-line";

let lastHighlightedText = "";

async function revealAndHighlightLine(lineNumber: number, searchText: string): Promise<boolean> {
	/*
	 *	If the same line is already highlighted, skip the scroll/re-highlight cycle
	 *	to avoid flashing between consecutive steps targeting the same line.
	 */
	if (lastHighlightedText === searchText && document.querySelector(`.${TOUR_LINE_CLASS}`)) {
		return false;
	}

	document.querySelector(`.${TOUR_LINE_CLASS}`)?.classList.remove(TOUR_LINE_CLASS);
	lastHighlightedText = "";

	const editor = (window as any).codeEditor;
	if (!editor) return false;

	const linePadding = 3;
	editor.revealLine(lineNumber - linePadding);
	await sleep(300);

	const editorDom = editor.getDomNode();
	if (!editorDom) return false;

	const viewLines = editorDom.querySelectorAll(".view-line");
	for (const vl of viewLines) {
		const text = vl.textContent?.replace(/\s+/g, " ") ?? "";
		if (text.includes(searchText)) {
			vl.classList.add(TOUR_LINE_CLASS);
			lastHighlightedText = searchText;
			break;
		}
	}
	return true;
}

export const shortTourSteps: TourStep[] = [
	{
		type: TourStepTypeE.Modal,
		title: "Welcome!",
		text: "The <strong>Signaloid Cloud Developer Platform</strong> is a development environment for running compute tasks on <strong>Signaloid's uncertainty-tracking processors</strong>.<br><br>You can also use the platform for building web-based applications that run compute tasks on the Signaloid compute engine through a REST API.",
		onNext: () => navigateTo("/launch"),
	},
	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-code-editor",
		title: "Code Playground",
		text: "This is the <strong>Code Playground</strong>. You can use it to write and run single-file applications on the Signaloid processor cores.<br><br>(Note: Clicking <strong>Next</strong> will replace your code in the code editor with a default example.)",
		tooltipLocation: "bottom",
		onNext: () => resetEditorCode(),
	},
	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-run-button-editor",
		title: "Compile and Run",
		text: "Click the <strong>Run</strong> button to compile and run the code in the editor.",
		interactive: true,
		tooltipLocation: "bottom",
	},
	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-results-panel-editor",
		title: "Results Panel",
		text: "The code will run and the results will appear in the results panel on the <code>stdout</code> tab.<br><br><strong><span class='tour-desktop-only'>Hover your mouse over</span><span class='tour-mobile-only'>Tap on</span> the number <code>0.010890</code> to see a plot of the distribution of <code>P</code></strong>.<br><br>The plot shows how the uncertainties in the resistance and circuit voltage (<code>R</code> and <code>V</code>) affect the uncertainty in the power dissipation of a resistor (<code>P</code>).",
		tooltipLocation: "top",
	},
	{
		type: TourStepTypeE.Modal,
		title: "Congratulations!",
		text: "<strong>You have run your first application on one of Signaloid uncertainty-tracking processor cores!</strong><br><br>Next steps:<ul><li><a href='https://docs.signaloid.io' target='_blank' rel='noopener'><strong>Dive into our documentation</strong></a> to learn how to create and deploy real-world applications that run on the Signaloid uncertainty-tracking processors.</li></ul>",
	},
];

export const SHORT_TOUR_ID = "QuickStart";

export const longTourSteps: TourStep[] = [
	{
		type: TourStepTypeE.Modal,
		title: "Welcome!",
		text: "The <strong>Signaloid Cloud Developer Platform</strong> is a development environment for launching compute tasks on <strong>Signaloid's uncertainty-tracking processors</strong>.<br><br>You can also use the developer platform as a launchpad for building web-based applications that run compute tasks on the Signaloid compute engine through a REST API.",
		onNext: () => navigateTo("/home"),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-sidebar-home",
		title: "Your Dashboard",
		text: "You are currently on the <strong>Home</strong> Tab.",
		tooltipLocation: "end",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-sidebar-toggle",
		title: "Sidebar Navigation",
		text: "Click here to open the <strong>Sidebar</strong>.",
		tooltipLocation: "bottom",
		mobileOnly: true,
		waitForClickSelector: ".userflow-sidebar-toggle",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-sidebar-cores",
		title: "Cores",
		text: "Click on the <strong>Cores</strong> Tab to see the range of Signaloid processor cores available.",
		tooltipLocation: "end",
		waitForRoute: "/cores",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-cores-heading-title",
		title: "Processor Cores",
		text: "You can <strong>access several processor core types</strong> with different performance and uncertainty-tracking quality.<br><br>You can also create custom cores optimized for the requirements of your applications.",
		tooltipLocation: "bottom",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-core-c0pro-s",
		title: "Run Applications on C0Pro-S",
		text: "This walkthrough will show you how to run a simple application on the <strong>C0Pro-S</strong> core, one of the default processor core types.",
		tooltipLocation: "bottom",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-sidebar-toggle",
		title: "Sidebar Navigation",
		text: "Click here to open the <strong>Sidebar</strong>.",
		tooltipLocation: "bottom",
		mobileOnly: true,
		waitForClickSelector: ".userflow-sidebar-toggle",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-sidebar-code-playground",
		title: "Code Playground",
		text: "Click on the <strong>Code Playground</strong> Tab.",
		tooltipLocation: "end",
		waitForRoute: "/launch",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-code-editor",
		title: "Code Editor",
		text: "You can use the code editor to compile single-file applications and execute them on the Signaloid processor cores.<br><br>(Note: Clicking <strong>Next</strong> will replace your code in the code editor with a default example.)",
		tooltipLocation: "bottom",
		onNext: () => resetEditorCode(),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-core-selector-editor",
		title: "Select a Core",
		text: "Click here to select a core to compile and run the code in the editor.",
		tooltipLocation: "bottom",
		waitForClickSelector: ".userflow-core-selector-editor",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-core-dialog-c0pro-s",
		title: "Select C0Pro-S Core",
		text: "Select the <strong>C0Pro-S</strong> core from the list of core types.",
		tooltipLocation: "end",
		waitForClickSelector: ".userflow-core-dialog-c0pro-s",
		beforeShow: async () => {
			await waitForElement(".userflow-core-dialog-c0pro-s", true);
			await sleep(400);
		},
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-core-selector-confirm-button",
		title: "Confirm Core Selection",
		text: "Click the <strong>Select Core</strong> button to confirm your selection.",
		tooltipLocation: "top",
		waitForClickSelector: ".userflow-core-selector-confirm-button",
		beforeShow: async () => {
			await waitForElement(".userflow-core-selector-confirm-button");
		},
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-run-button-editor",
		title: "Compile and Run",
		text: "Click the <strong>Run</strong> button to compile the code in the editor and run it on the selected core.",
		tooltipLocation: "bottom",
		waitForClickSelector: ".userflow-run-button-editor",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-editor-control-bar",
		title: "Build Progress",
		text: "The code will run on the <strong>C0Pro-S</strong> core and the results will appear in the results panel on the <strong><code>stdout</code> tab.</strong>",
		tooltipLocation: "top",
		interactive: true,
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: ".userflow-results-panel-editor",
		title: "Explore Results",
		text: "<strong><span class='tour-desktop-only'>Hover over</span><span class='tour-mobile-only'>Tap on</span> the numbers in the output</strong>, to see how the uncertainties in the resistance and circuit voltage (<code>R</code> and <code>V</code>) affect the uncertainty in the power dissipation of a resistor (<code>P</code>).",
		tooltipLocation: "top",
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: `.${TOUR_LINE_CLASS}`,
		title: "Reading Input Data",
		text: "This program <strong>reads data from a file,</strong> <code>example.csd</code>, <strong>using the standard C-language I/O</strong> <code>fscanf</code> <strong>routine.</strong><br><br>Signaloid processors are able to interpret this information and <strong>construct a <em>complete probability distribution</em></strong> of your input data uncertainty (not just summary statistics like mean and variance).",
		tooltipLocation: "bottom",
		beforeShow: () => revealAndHighlightLine(42, "fscanf"),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: `.${TOUR_LINE_CLASS}`,
		title: "Efficiency and Automation",
		text: "Signaloid processors can then <strong>efficiently and automatically</strong>, keep track of how these uncertainties are transformed by the arithmetic operations in the program.",
		tooltipLocation: "bottom",
		beforeShow: () => revealAndHighlightLine(52, "pow(V, 2)"),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: `.${TOUR_LINE_CLASS}`,
		title: "No Code Changes Needed",
		text: "The best part is, <strong>you don't need to make <em>any</em> changes to your code</strong> to achieve this.<br><br>You can simply write your code in the most intuitive way and let Signaloid processors automatically track how uncertainties in your inputs affect the computations of your application.",
		tooltipLocation: "bottom",
		beforeShow: () => revealAndHighlightLine(52, "pow(V, 2)"),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: `.${TOUR_LINE_CLASS}`,
		title: "Traditional Approach",
		text: "To achieve this on a <strong>traditional processor</strong>, you would need to perform a <strong>tedious rewrite of your application</strong> to implement <strong>Monte Carlo techniques.</strong>",
		tooltipLocation: "bottom",
		beforeShow: () => revealAndHighlightLine(52, "pow(V, 2)"),
	},

	{
		type: TourStepTypeE.Tooltip,
		targetSelector: `.${TOUR_LINE_CLASS}`,
		title: "Performance",
		text: "You would then need to run your application in a <strong>time-consuming repeated Monte Carlo iteration and post-process your results manually</strong> to see how input data uncertainties affect the output.<br><br>For reference, the Signaloid <em>C0-M+</em> processor can run this application <strong>more than 10x faster</strong> than running <strong>Monte Carlo</strong> on a Intel Gold 6455B <strong>while achieving the same accuracy.</strong>",
		tooltipLocation: "bottom",
		beforeShow: () => revealAndHighlightLine(52, "pow(V, 2)"),
	},

	{
		type: TourStepTypeE.Modal,
		title: "Congratulations!",
		text: "<strong>You have run an application on one of Signaloid uncertainty-tracking processor cores!</strong><br><br>Next steps:<ul><li><a href='https://docs.signaloid.io' target='_blank' rel='noopener'><strong>Dive into our documentation</strong></a> to learn how to create and deploy real-world applications that run on the Signaloid uncertainty-tracking processors.</li></ul>",
	},
];

export const LONG_TOUR_ID = "Detailed";
